// IngredientSheet — per-product ingredient sheet (bottom sheet on mobile). The verdict pill at the top is
// the contract-C scale (i18n verdictLevel): the number of flagged items, in its colour.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { fill, findingsOf, flaggedLine, nounFor, useLang, verdictLabel, verdictLevel } from "./i18n";
import { useMessages } from "./messages";
import { signal } from "./pulse";

const API_BASE = "/api/bloom";
const SITE = "https://bloom.obto.co";

export type Level = "low" | "moderate" | "high" | "unknown";

type Item = { name: string; role: string; level: Level; note: string; tags: string[] };
export type Data = {
    ok: boolean;
    kind?: "check";
    checkId?: string;
    lang?: string;
    status: "ok" | "none" | "pending" | "error";
    title?: string;
    brand?: string | null;
    imageUrl?: string | null;
    overall?: Level;
    counts?: Record<Level, number>;
    // Contract rule C: flagged items on the sheet (the server counts them; older answers fall back to counts).
    findings?: number;
    summary?: string;
    analyzedAt?: string;
    goUrl?: string;
    items?: Item[];
    // "Check a label" from a product name, a link or a front-of-pack photo: where the list was found.
    research?: { product: string; url: string; host: string; kind: string; corroborated: string[]; note: string } | null;
    // B21, an Amazon link or ASIN: onBloom = on Bloom (verified, or superseded); shareUrl = its product page.
    asin?: string | null; shareUrl?: string; amazonBusy?: boolean;
    onBloom?: { asin: string; status: "verified" | "superseded"; productType: string | null } | null;
    // B24: "materials" when every item is a fibre or material (the count reads "1 material"); the standard the
    // product met on Bloom ("clean" shows "Clean formula · no red flags").
    listKind?: string | null; standard?: string | null;
};

export const LEVEL_STYLE: Record<Level, { dot: string; text: string; soft: string; ring: string; stroke: string; bar: string }> = {
    high: { dot: "bg-rose-500", text: "text-rose-700", soft: "bg-rose-50", ring: "ring-rose-200", stroke: "#f43f5e", bar: "bg-rose-500" },
    moderate: { dot: "bg-amber-500", text: "text-amber-700", soft: "bg-amber-50", ring: "ring-amber-200", stroke: "#f59e0b", bar: "bg-amber-400" },
    unknown: { dot: "bg-slate-400", text: "text-slate-600", soft: "bg-slate-50", ring: "ring-slate-200", stroke: "#94a3b8", bar: "bg-slate-300" },
    low: { dot: "bg-emerald-500", text: "text-emerald-700", soft: "bg-emerald-50", ring: "ring-emerald-200", stroke: "#10b981", bar: "bg-emerald-400" },
};
const ORDER: Level[] = ["high", "moderate", "unknown", "low"];

function Donut({ counts, total, label }: { counts: Record<Level, number>; total: number; label: string }) {
    const [drawn, setDrawn] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => setDrawn(true));
        return () => cancelAnimationFrame(id);
    }, []);
    const r = 42;
    const c = 2 * Math.PI * r;
    let offset = 0;
    return (
        <div className="relative h-36 w-36 shrink-0">
            <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                <circle cx="50" cy="50" r={r} fill="none" stroke="#f1f5f9" strokeWidth="10" />
                {ORDER.map((lvl, i) => {
                    const n = counts[lvl] || 0;
                    if (!n || !total) return null;
                    const len = (n / total) * c;
                    const seg = (
                        <circle
                            key={lvl}
                            cx="50" cy="50" r={r} fill="none"
                            stroke={LEVEL_STYLE[lvl].stroke}
                            strokeWidth="10"
                            strokeLinecap="butt"
                            strokeDasharray={drawn ? `${len} ${c}` : `0 ${c}`}
                            strokeDashoffset={-offset}
                            style={{ transition: `stroke-dasharray 900ms cubic-bezier(.2,.8,.2,1) ${i * 120}ms` }}
                        />
                    );
                    offset += len;
                    return seg;
                })}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-extrabold tabular-nums text-gray-900">{total}</span>
                <span className="text-[11px] font-medium text-gray-500">{label}</span>
            </div>
        </div>
    );
}

function WhatsAppIcon() {
    return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
            <path d="M12.04 2a9.9 9.9 0 0 0-8.5 15l-1.4 5 5.1-1.3A9.9 9.9 0 1 0 12.04 2Zm0 18.1c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3a8.2 8.2 0 1 1 7 3.8Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.3.8 3.2.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3Z" />
        </svg>
    );
}

function ShareBar({ url, title, counts, listKind, refKey }: { url: string; title: string; counts?: Record<Level, number>; listKind?: string | null; refKey: { a?: string; c?: string } }) {
    const { t } = useLang();
    const [copied, setCopied] = useState(false);
    // Product pages read utm_* in the browser (pulse.tsx); the label-check share page reads s.
    const withSrc = (ch: string) => url + (url.includes("?") ? "&" : "?") + (url.includes("/share/") ? "s=" + ch : "utm_source=" + ch + "&utm_medium=share");
    // The last line invites the friend to check their own product (the link follows it).
    const text = t.shareLead + " " + title + " — " + flaggedLine(t, counts, listKind) + "\n" + t.shareInvite;
    const canNative = typeof navigator !== "undefined" && typeof navigator.share === "function";
    const open = (ch: string, href: string) => {
        signal("share_tap", { ...refKey, ch });
        window.open(href, "_blank", "noopener,noreferrer");
    };
    const copy = async () => {
        signal("share_tap", { ...refKey, ch: "copy" });
        try {
            await navigator.clipboard.writeText(withSrc("copy"));
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        } catch (e) {}
    };
    const native = async () => {
        signal("share_tap", { ...refKey, ch: "native" });
        try { await navigator.share({ title, text, url: withSrc("native") }); } catch (e) {}
    };
    const btn = "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold shadow-sm transition active:scale-95";
    return (
        <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">{t.share}</span>
            <button type="button" onClick={() => open("whatsapp", "https://wa.me/?text=" + encodeURIComponent(text + "\n" + withSrc("whatsapp")))} className={btn + " bg-[#25D366] text-white hover:brightness-95"}>
                <WhatsAppIcon /> WhatsApp
            </button>
            <button type="button" onClick={() => open("x", "https://twitter.com/intent/tweet?text=" + encodeURIComponent(text) + "&url=" + encodeURIComponent(withSrc("x")))} className={btn + " bg-gray-900 text-white hover:bg-black"}>
                <span className="text-sm leading-none">𝕏</span>
            </button>
            <button type="button" onClick={copy} className={btn + " bg-white text-gray-800 ring-1 ring-gray-200 hover:ring-gray-300"}>
                {copied ? t.linkCopied : t.copyLink}
            </button>
            {canNative ? (
                <button type="button" onClick={native} className={btn + " bg-white text-gray-800 ring-1 ring-gray-200 hover:ring-gray-300"}>
                    {t.moreShare}…
                </button>
            ) : null}
        </div>
    );
}

function ReportBox({ refId, items }: { refId: string; items: Item[] }) {
    const { t, lang } = useLang();
    const [open, setOpen] = useState(false);
    const [msg, setMsg] = useState("");
    const [item, setItem] = useState("");
    const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");

    if (state === "done") return <p className="mt-4 text-center text-xs font-medium text-emerald-700">{t.reportThanks}</p>;
    if (!open) {
        return (
            <button
                type="button"
                onClick={() => { setOpen(true); signal("report_open", refId.length === 10 ? { a: refId } : { c: refId }); }}
                className="mx-auto mt-4 block text-xs font-medium text-gray-400 underline decoration-dotted underline-offset-4 hover:text-gray-700"
            >
                {t.somethingWrong}
            </button>
        );
    }
    const send = async () => {
        if (msg.trim().length < 3) return;
        setState("sending");
        try {
            const res = await fetch(`${API_BASE}/report`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ ref: refId, item, msg, l: lang }),
            });
            const ctype = res.headers.get("content-type") || "";
            if (!res.ok || !ctype.includes("application/json")) throw new Error(String(res.status));
            setState("done");
        } catch (e) {
            setState("error");
        }
    };
    return (
        <div className="mt-4 rounded-xl bg-white p-3 ring-1 ring-gray-200 animate-in fade-in duration-200">
            <p className="text-sm font-semibold text-gray-800">{t.reportTitle}</p>
            {items.length ? (
                <select value={item} onChange={(e) => setItem(e.target.value)} className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-700">
                    <option value="">{t.reportItem}</option>
                    {items.map((it) => <option key={it.name} value={it.name}>{it.name}</option>)}
                </select>
            ) : null}
            <textarea
                value={msg}
                onChange={(e) => setMsg(e.target.value.slice(0, 600))}
                rows={3}
                placeholder={t.reportPlaceholder}
                className="mt-2 w-full resize-none rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-800 focus:border-green-500 focus:outline-none"
            />
            <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-[11px] text-red-600">{state === "error" ? t.reportError : ""}</span>
                <button
                    type="button"
                    onClick={send}
                    disabled={state === "sending" || msg.trim().length < 3}
                    className="rounded-lg bg-gray-900 px-4 py-1.5 text-xs font-bold text-white transition hover:bg-black disabled:opacity-40"
                >
                    {t.send}
                </button>
            </div>
        </div>
    );
}

function OnBloomNote({ status }: { status: "verified" | "superseded" }) { // B21: Check a label, product already on Bloom
    const m = useMessages();
    return (
        <div className="mt-4 rounded-2xl bg-emerald-50 p-3 text-xs leading-relaxed text-emerald-900 ring-1 ring-emerald-100">
            <p className="flex items-center gap-1.5 font-bold"><span aria-hidden="true">✓</span>{m.verifiedByBloom}</p>
            <p className="mt-1 text-emerald-800/90">{status === "verified" ? m.onBloomBody : m.onBloomSuperseded}</p>
        </div>
    );
}

// The source of a list Bloom looked up online, cited like a careful person would cite it.
function SourceNote({ r, busy }: { r: NonNullable<Data["research"]>; busy?: boolean }) {
    const { lang } = useLang();
    const m = useMessages();
    return (
        <div className="mt-4 rounded-2xl bg-sky-50 p-3 text-xs leading-relaxed text-sky-900 ring-1 ring-sky-100">
            <p className="flex items-center gap-1.5 font-bold"><span aria-hidden="true">🔎</span>{m.foundOnline}</p>
            <a
                href={r.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="mt-1 inline-flex max-w-full items-center gap-1 font-semibold text-sky-800 underline decoration-sky-300 underline-offset-2"
            >
                <span className="truncate">{fill(m.fromSource, { host: r.host })}</span>
                <span className="shrink-0 font-normal text-sky-700/80">· {m.sourceKind[r.kind] || m.sourceKind.web}</span>
                <span aria-hidden="true">↗</span>
            </a>
            {busy ? <p className="mt-0.5 font-semibold text-sky-900">{m.amazonBusy}</p> : null}
            {r.corroborated.length ? <p className="mt-0.5 text-sky-800/80">{fill(m.alsoOn, { hosts: r.corroborated.join(", ") })}</p> : null}
            {r.note && lang === "en" ? <p className="mt-0.5 text-sky-800/80">{r.note}</p> : null}
            <p className="mt-1 text-sky-800/80">{m.formulaNote}</p>
        </div>
    );
}

type SheetProps = {
    asin?: string;
    checkId?: string;
    initial?: Data | null;
    onClose: () => void;
    onCheckAnother?: () => void;
    // A visitor who opened someone else's shared result: the button invites them to check their own.
    arrived?: boolean;
};

export default function IngredientSheet({ asin, checkId, initial, onClose, onCheckAnother, arrived }: SheetProps) {
    const { t, lang } = useLang();
    const [data, setData] = useState<Data | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<Level | "all">("all");
    const [open, setOpen] = useState<Record<number, boolean>>({});
    const closeRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        let cancelled = false;
        const accept = (d: Data) => {
            setData(d);
            const init: Record<number, boolean> = {};
            (d.items || []).forEach((it, i) => { if (it.level === "high" || it.level === "moderate") init[i] = true; });
            setOpen(init);
        };
        setError(null);
        if (initial && initial.lang === lang) {
            accept(initial);
            return;
        }
        setData(null);
        const path = checkId ? `check/${encodeURIComponent(checkId)}` : `ingredients/${encodeURIComponent(asin || "")}`;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/${path}?lang=${lang}`, { headers: { Accept: "application/json" } });
                const ctype = res.headers.get("content-type") || "";
                if (!res.ok || !ctype.includes("application/json")) throw new Error(`GET ${API_BASE}/${path.split("/")[0]} returned ${res.status}`);
                const d: Data = await res.json();
                if (!cancelled) accept(d);
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : String(e));
            }
        })();
        return () => { cancelled = true; };
    }, [asin, checkId, initial, lang]);

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        closeRef.current?.focus();
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    const items = data?.items || [];
    const counts = (data?.counts || { low: 0, moderate: 0, high: 0, unknown: 0 }) as Record<Level, number>;
    const total = items.length;
    const shown = useMemo(
        () => items.map((it, i) => ({ it, i })).filter(({ it }) => filter === "all" || it.level === filter),
        [items, filter]
    );
    const findings = typeof data?.findings === "number" ? data.findings : findingsOf(counts);
    const verdict = verdictLevel(findings);
    const refKey = checkId ? { c: checkId } : { a: asin };
    const shareUrl = data?.shareUrl
        ? data.shareUrl
        : checkId
        ? `${SITE}/api/bloom/share/c/${checkId}?lang=${lang}`
        : `${SITE}/api/bloom/p/${asin}${lang === "en" ? "" : "?lang=" + lang}`;

    return (
        <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label={t.whatsInside}>
            <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose} />
            <div
                className={
                    "absolute inset-x-0 bottom-0 flex max-h-[88vh] flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl " +
                    "animate-in max-md:slide-in-from-bottom duration-300 " +
                    "md:inset-y-0 md:left-auto md:right-0 md:max-h-none md:w-[460px] md:rounded-t-none md:rounded-l-3xl md:slide-in-from-right"
                }
            >
                <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-gray-200 md:hidden" />

                {/* Header */}
                <div className="relative bg-gradient-to-br from-green-50 via-white to-emerald-50 px-5 pb-5 pt-4">
                    <button
                        ref={closeRef}
                        onClick={onClose}
                        aria-label={t.close}
                        className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white text-xl text-gray-500 shadow ring-1 ring-gray-100 transition hover:rotate-90 hover:text-gray-900"
                    >
                        &times;
                    </button>
                    <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-green-700">
                        {t.whatsInside}
                        {checkId ? <span className="rounded-full bg-green-600 px-2 py-0.5 text-[10px] tracking-wide text-white">{t.yourLabel}</span> : null}
                    </p>
                    <div className="mt-2 flex items-center gap-3 pr-10">
                        {data?.imageUrl ? (
                            <img src={data.imageUrl} alt="" className="h-12 w-12 rounded-xl object-cover ring-1 ring-gray-100" />
                        ) : null}
                        <div className="min-w-0">
                            <h2 className="line-clamp-2 text-base font-bold leading-snug text-gray-900">{data?.title || " "}</h2>
                            {data?.brand ? <p className="truncate text-xs text-gray-500">{data.brand}</p> : null}
                        </div>
                    </div>

                    {data?.status === "ok" ? (
                        <div className="mt-4 flex items-center gap-4">
                            <Donut counts={counts} total={total} label={nounFor(t, total, data.listKind)} />
                            <div className="min-w-0 flex-1">
                                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${verdict.soft} ${verdict.text} ${verdict.ring}`}>
                                    <span className={`h-2 w-2 rounded-full ${verdict.dot}`} />
                                    {verdictLabel(t, findings)}
                                </span>
                                {data.standard === "clean" ? (
                                    <span className="ml-1.5 inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-800 ring-1 ring-emerald-200">{t.cleanFormula}</span>
                                ) : null}
                                {data.summary ? <p className="mt-2 text-sm leading-relaxed text-gray-700">{data.summary}</p> : null}
                            </div>
                        </div>
                    ) : null}
                    {data?.status === "ok" ? <ShareBar url={shareUrl} title={data.title || ""} counts={data.counts} listKind={data.listKind} refKey={refKey} /> : null}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 pb-5">
                    {error ? <p className="py-8 text-sm text-red-700">{error}</p> : null}
                    {!data && !error ? (
                        <div className="space-y-3 py-6">
                            {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-gray-100" />)}
                        </div>
                    ) : null}
                    {data && data.status !== "ok" ? (
                        <p className="py-8 text-center text-sm text-gray-500">{data.status === "none" ? t.noIngredients : t.pending}</p>
                    ) : null}

                    {data?.status === "ok" ? (
                        <>
                            {data.onBloom ? <OnBloomNote status={data.onBloom.status} /> : null}
                            {data.research ? <SourceNote r={data.research} busy={data.amazonBusy} /> : null}
                            <div className="sticky top-0 z-10 -mx-5 flex gap-2 overflow-x-auto bg-white/95 px-5 py-3 backdrop-blur">
                                {(["all", ...ORDER] as (Level | "all")[]).map((lvl) => {
                                    const n = lvl === "all" ? total : counts[lvl] || 0;
                                    if (lvl !== "all" && !n) return null;
                                    const active = filter === lvl;
                                    const st = lvl === "all" ? null : LEVEL_STYLE[lvl];
                                    return (
                                        <button
                                            key={lvl}
                                            onClick={() => setFilter(lvl)}
                                            className={
                                                "flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition-all " +
                                                (active ? "bg-gray-900 text-white ring-gray-900 shadow" : "bg-white text-gray-700 ring-gray-200 hover:ring-gray-300")
                                            }
                                        >
                                            {st ? <span className={`h-2 w-2 rounded-full ${st.dot}`} /> : null}
                                            {lvl === "all" ? t.all : t.level[lvl]}
                                            <span className={active ? "text-white/70" : "text-gray-400"}>{n}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="mb-3 text-[11px] text-gray-400">{t.labelOrder}</p>

                            <ul className="space-y-2">
                                {shown.map(({ it, i }, k) => {
                                    const st = LEVEL_STYLE[it.level];
                                    const isOpen = !!open[i];
                                    return (
                                        <li
                                            key={it.name}
                                            className="animate-in fade-in slide-in-from-bottom-2 duration-300"
                                            style={{ animationDelay: `${Math.min(k, 12) * 35}ms`, animationFillMode: "both" }}
                                        >
                                            <button
                                                onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}
                                                aria-expanded={isOpen}
                                                className="group relative flex w-full overflow-hidden rounded-xl bg-white text-left ring-1 ring-gray-100 transition hover:shadow-md hover:ring-gray-200"
                                            >
                                                <span className={`w-1.5 shrink-0 ${st.bar}`} />
                                                <span className="min-w-0 flex-1 px-3 py-2.5">
                                                    <span className="flex items-start justify-between gap-2">
                                                        <span className="min-w-0">
                                                            <span className="block truncate text-sm font-semibold text-gray-900">{it.name}</span>
                                                            <span className="block truncate text-xs text-gray-500">{it.role}</span>
                                                        </span>
                                                        <span className={`mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${st.soft} ${st.text}`}>
                                                            <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
                                                            {t.level[it.level]}
                                                        </span>
                                                    </span>
                                                    {isOpen ? (
                                                        <span className="mt-2 block animate-in fade-in duration-200">
                                                            <span className="block text-[13px] leading-relaxed text-gray-700">{it.note}</span>
                                                            {it.tags.length ? (
                                                                <span className="mt-2 flex flex-wrap gap-1">
                                                                    {it.tags.map((tag) => (
                                                                        <span key={tag} className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                                                                            {t.tags[tag] || tag}
                                                                        </span>
                                                                    ))}
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                    ) : null}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>

                            <div className="mt-5 rounded-xl bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-500">
                                <p className="mb-1.5 grid grid-cols-2 gap-x-3 gap-y-1">
                                    {ORDER.map((lvl) => (
                                        <span key={lvl} className="flex items-start gap-1.5">
                                            <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${LEVEL_STYLE[lvl].dot}`} />
                                            <span><b className="font-semibold text-gray-600">{t.level[lvl]}</b> — {t.levelHint[lvl]}</span>
                                        </span>
                                    ))}
                                </p>
                                <p className="mt-2">{t.aiNote}</p>
                                {data.analyzedAt ? (
                                    <p className="mt-1">{t.analysed}: {new Date(data.analyzedAt).toLocaleDateString(lang)}</p>
                                ) : null}
                            </div>
                            <ReportBox key={(checkId || asin || "") + lang} refId={checkId || asin || ""} items={items} />
                        </>
                    ) : null}
                </div>

                {checkId && onCheckAnother ? (
                    <div className="border-t border-gray-100 bg-white p-4">
                        <button
                            type="button"
                            onClick={onCheckAnother}
                            className="block w-full rounded-xl bg-green-600 py-3 text-center text-sm font-bold text-white shadow-lg shadow-green-600/20 transition hover:bg-green-700 active:scale-[.99]"
                        >
                            {arrived ? "📸 " + t.checkYours : t.checkAnother}
                        </button>
                    </div>
                ) : null}
                {data?.goUrl ? (
                    <div className="border-t border-gray-100 bg-white p-4">
                        <a
                            href={data.goUrl}
                            onClick={() => signal("amazon_tap", { a: asin || data?.asin || undefined })}
                            target="_blank"
                            rel="sponsored nofollow noopener noreferrer"
                            data-obto-cta="amazon"
                            className="block rounded-xl bg-green-600 py-3 text-center text-sm font-bold text-white shadow-lg shadow-green-600/20 transition hover:bg-green-700 active:scale-[.99]"
                        >
                            {t.checkPrice}
                        </a>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
